import React, { useState, useEffect, useMemo, useRef } from 'react';
import { FaFileExport, FaExternalLinkAlt } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import {
    flattenSurveys, headlineCounts, filterSurveys, unmatchableApproved, defaultSelection
} from '../../bulkReview';
import { exportSurveys } from '../../functions/surveyExport';
import { formatDateTimeShort as formatDate } from '../../i18n/format';

// Values are the server's layout names; labels are translation keys.
const LAYOUTS = [
    { value: 'Columns', label: 'bulk.layoutColumns' },
    { value: 'Heading', label: 'bulk.layoutHeading' },
    { value: 'Metadata', label: 'bulk.layoutMetadata' }
];

export const BulkReview = () => {
    const { t } = useTranslation();
    const [map, setMap] = useState(null);
    const [error, setError] = useState(null);
    const [days, setDays] = useState(1);
    const [mode, setMode] = useState('uploaded');
    const [search, setSearch] = useState(null);
    const [selected, setSelected] = useState(new Set());
    const [layout, setLayout] = useState('Columns');
    const [exporting, setExporting] = useState(false);
    const [exportError, setExportError] = useState('');

    useEffect(() => {
        let cancelled = false;
        fetch('api/projects')
            .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
            .then(data => { if (!cancelled) setMap(data); })
            .catch(err => { if (!cancelled) setError(err.message); });
        return () => { cancelled = true; };
    }, []);

    // The admin picks the layout a bulk export starts on; the user may change it
    // for a single export, but nothing else about the template is theirs.
    useEffect(() => {
        let cancelled = false;
        fetch('api/identity/customersettings')
            .then(r => r.ok ? r.json() : null)
            .then(settings => {
                if (cancelled || !settings || !settings.exportTemplate) return;
                const preferred = settings.exportTemplate.defaultLayout;
                if (LAYOUTS.some(l => l.value === preferred)) setLayout(preferred);
            })
            .catch(() => { });
        return () => { cancelled = true; };
    }, []);

    const rows = useMemo(() => flattenSurveys(map), [map]);
    const counts = useMemo(
        () => (map ? headlineCounts(rows, Date.now()) : null), [rows, map]);

    // Results are only recomputed when Search is pressed, so ticking a box does
    // not silently re-run the query underneath the user.
    const results = search ? search.results : null;

    // Re-derive the ticks only when the *search* changes, not on every render.
    // Otherwise an unrelated state change would re-tick rows the user had
    // deliberately cleared - the same trap SurveyPreviewTable guards against.
    const searchKey = search ? search.key : null;
    const initialisedKey = useRef(null);
    useEffect(() => {
        if (results && initialisedKey.current !== searchKey) {
            setSelected(defaultSelection(results));
            initialisedKey.current = searchKey;
        }
    }, [results, searchKey]);

    const runSearch = () => {
        setExportError('');
        const now = Date.now();
        setSearch({
            key: `${mode}:${days}:${now}`,
            mode,
            days,
            results: filterSurveys(rows, { days, mode }, now),
            skipped: unmatchableApproved(rows, mode)
        });
    };

    const toggle = (fileId) => {
        setSelected(previous => {
            const next = new Set(previous);
            if (next.has(fileId)) next.delete(fileId); else next.add(fileId);
            return next;
        });
    };

    const selectedCount = results ? results.filter(r => selected.has(r.fileId)).length : 0;
    const allTicked = results && results.length > 0 && selectedCount === results.length;

    const toggleAll = () => {
        if (!results) return;
        setSelected(allTicked ? new Set() : new Set(results.map(r => r.fileId)));
    };

    const handleExport = async () => {
        if (!results) return;
        setExporting(true);
        setExportError('');
        try {
            const fileIds = results.filter(r => selected.has(r.fileId)).map(r => r.fileId);
            await exportSurveys(fileIds, layout, 'survey-export.csv');
        } catch (err) {
            setExportError(err.message);
        }
        setExporting(false);
    };

    // Approved surveys live under a drillhole, so the link opens the hole rather
    // than the survey: there is no URL that selects a single approved survey, and
    // the Table tab's picker is where you choose one. An unapproved survey has no
    // drillhole yet, so it goes to the approval page instead.
    const linkFor = (row) => row.approved
        ? `/overview?project=${encodeURIComponent(row.project)}&drillhole=${encodeURIComponent(row.drillhole)}`
        : `/approval?survey=${encodeURIComponent(row.fileName)}`;

    if (error) {
        return (
            <div className="container" style={{ marginTop: 10 }}>
                <h3>{t('bulk.title')}</h3>
                <div className="alert alert-danger">{t('bulk.loadFailed', { error })}</div>
            </div>
        );
    }

    return (
        <div className="container-fluid" style={{ marginTop: 10 }}>
            <h3>{t('bulk.title')}</h3>
            <p className="text-muted">
                {t('bulk.intro')}
            </p>

            {!map && <p><em>{t('charts.loadingSurveys')}</em></p>}

            {counts && (
                <>
                    <div className="row g-3 mb-3">
                        {/* Plain bordered panels rather than .card, which custom.css
                            themes solid red app-wide. */}
                        <Panel title={t('bulk.uploaded')} counts={counts.uploaded} />
                        <Panel title={t('bulk.approved')} counts={counts.approved}
                            note={counts.approvedDateUnknown > 0
                                ? t('bulk.approvedDateUnknown', { count: counts.approvedDateUnknown })
                                : null} />
                        <div className="col-md-4">
                            <div className="border rounded bg-white p-3 h-100">
                                <h6 className="text-muted mb-2">{t('bulk.allSurveys')}</h6>
                                <div className="d-flex justify-content-between">
                                    <span>{t('bulk.total')}</span><strong>{counts.total}</strong>
                                </div>
                                <div className="d-flex justify-content-between">
                                    <span>{t('bulk.approved')}</span><strong>{counts.approvedTotal}</strong>
                                </div>
                                <div className="d-flex justify-content-between">
                                    <span>{t('bulk.awaitingApproval')}</span><strong>{counts.unapprovedTotal}</strong>
                                </div>
                            </div>
                        </div>
                    </div>
                    <p className="text-muted small">
                        {t('bulk.serverTime')}
                    </p>

                    <div className="border rounded bg-white p-3 mb-3">
                        <div className="row g-2 align-items-end">
                            <div className="col-auto">
                                <label className="form-label mb-1" htmlFor="bulk-mode">{t('bulk.findSurveys')}</label>
                                <select id="bulk-mode" className="form-select form-select-sm"
                                    value={mode} onChange={e => setMode(e.target.value)}>
                                    <option value="uploaded">{t('bulk.modeUploaded')}</option>
                                    <option value="approved">{t('bulk.modeApproved')}</option>
                                    <option value="either">{t('bulk.modeEither')}</option>
                                </select>
                            </div>
                            <div className="col-auto">
                                <label className="form-label mb-1" htmlFor="bulk-days">{t('bulk.inTheLast')}</label>
                                <div className="input-group input-group-sm">
                                    <input id="bulk-days" type="number" min="1" max="3650"
                                        className="form-control" style={{ width: 90 }}
                                        value={days}
                                        onChange={e => setDays(Math.max(1, parseInt(e.target.value, 10) || 1))} />
                                    <span className="input-group-text">{t('bulk.days')}</span>
                                </div>
                            </div>
                            <div className="col-auto">
                                <button className="btn btn-primary btn-sm" onClick={runSearch}>{t('bulk.search')}</button>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {exportError && (
                <div className="alert alert-danger alert-dismissible" role="alert">
                    {exportError}
                    <button type="button" className="btn-close" onClick={() => setExportError('')}></button>
                </div>
            )}

            {results && (
                <div className="border rounded bg-white p-3">
                    <div className="d-flex align-items-center flex-wrap mb-2">
                        <h5 className="mb-0">
                            {t('bulk.found', { count: results.length })}
                        </h5>
                        <span className="text-muted ms-3">{t('bulk.selected', { number: selectedCount })}</span>
                        <div className="ms-auto d-flex align-items-center">
                            <select className="form-select form-select-sm" style={{ minWidth: 340 }}
                                value={layout} onChange={e => setLayout(e.target.value)}>
                                {LAYOUTS.map(l => (
                                    <option key={l.value} value={l.value}>{t(l.label)}</option>
                                ))}
                            </select>
                            <button className="btn btn-primary btn-sm ms-2"
                                disabled={selectedCount === 0 || exporting}
                                onClick={handleExport}>
                                <FaFileExport /> {exporting ? t('bulk.exporting') : t('bulk.exportCsv')}
                            </button>
                        </div>
                    </div>

                    {search.skipped > 0 && (
                        <div className="alert alert-info py-2" role="alert">
                            {t('bulk.skipped', { count: search.skipped })}
                        </div>
                    )}

                    {results.length === 0 ? (
                        <p className="text-muted"><em>{t('bulk.nothingMatched')}</em></p>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-striped table-sm align-middle">
                                <thead>
                                    <tr>
                                        <th style={{ width: 90 }}>
                                            {t('bulk.columnExport')}
                                            <input type="checkbox" className="ms-2"
                                                title={allTicked ? t('approval.deselectAll') : t('approval.selectAll')}
                                                checked={!!allTicked} onChange={toggleAll} />
                                        </th>
                                        <th>{t('bulk.columnSurvey')}</th>
                                        <th>{t('bulk.columnProject')}</th>
                                        <th>{t('bulk.columnDrillhole')}</th>
                                        <th>{t('bulk.columnUploaded')}</th>
                                        <th>{t('bulk.columnApproved')}</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {results.map(row => {
                                        const approvedOn = formatDate(row.approvedAt);
                                        return (
                                            <tr key={row.fileId}>
                                                <td>
                                                    <input type="checkbox"
                                                        checked={selected.has(row.fileId)}
                                                        onChange={() => toggle(row.fileId)} />
                                                </td>
                                                <td>
                                                    {row.fileName}
                                                    {!row.approved && (
                                                        <span className="badge bg-warning text-dark ms-2"
                                                            title={t('bulk.notApprovedTitle')}>
                                                            {t('bulk.notApproved')}
                                                        </span>
                                                    )}
                                                </td>
                                                <td>{row.project || <span className="text-muted">&mdash;</span>}</td>
                                                <td>{row.drillhole || <span className="text-muted">&mdash;</span>}</td>
                                                <td>{formatDate(row.uploaded) || <span className="text-muted">&mdash;</span>}</td>
                                                <td>
                                                    {!row.approved
                                                        ? <span className="text-muted">&mdash;</span>
                                                        : approvedOn
                                                            ? <>{approvedOn}{row.approvedBy ? <span className="text-muted"> {t('bulk.approvedBy', { name: row.approvedBy })}</span> : null}</>
                                                            : <span className="text-muted"
                                                                title={t('bulk.dateUnknownTitle')}>
                                                                {t('bulk.dateUnknown')}
                                                            </span>}
                                                </td>
                                                <td style={{ whiteSpace: 'nowrap' }}>
                                                    <a className="btn btn-sm btn-link"
                                                        href={linkFor(row)}
                                                        target="_blank" rel="noopener noreferrer">
                                                        {row.approved ? t('bulk.navigateToDrillhole') : t('bulk.openInApproval')}
                                                        {' '}<FaExternalLinkAlt />
                                                    </a>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const Panel = ({ title, counts, note }) => {
    const { t } = useTranslation();
    return (
        <div className="col-md-4">
            <div className="border rounded bg-white p-3 h-100">
                <h6 className="text-muted mb-2">{title}</h6>
                <div className="d-flex justify-content-between"><span>{t('bulk.last24Hours')}</span><strong>{counts.day}</strong></div>
                <div className="d-flex justify-content-between"><span>{t('bulk.last7Days')}</span><strong>{counts.week}</strong></div>
                <div className="d-flex justify-content-between"><span>{t('bulk.last30Days')}</span><strong>{counts.month}</strong></div>
                {note && <div className="small text-muted mt-2">{note}</div>}
            </div>
        </div>
    );
};
